// Core UI components (§9.9): Button, IconButton, Card, Drawer, BottomSheet, Tabs, Toggle, Slider, Stepper,
// Meter, Stamp, Modal, ProgressPips, LabLog. All targets ≥ 44 px; every control has a keyboard path.
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useId, useRef } from 'preact/hooks';
import './ui.css';

type Tone = 'primary' | 'secondary' | 'ghost' | 'danger' | 'bureau';

export function Button(props: {
  children: ComponentChildren;
  onClick?: (() => void) | undefined;
  tone?: Tone | undefined;
  disabled?: boolean | undefined;
  type?: 'button' | 'submit' | undefined;
  class?: string | undefined;
  title?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-pressed'?: boolean | undefined;
  autoFocus?: boolean | undefined;
  big?: boolean | undefined;
}): JSX.Element {
  return (
    <button
      type={props.type ?? 'button'}
      class={`btn btn-${props.tone ?? 'secondary'}${props.big ? ' btn-big' : ''}${props.class ? ` ${props.class}` : ''}`}
      onClick={props.onClick}
      disabled={props.disabled}
      title={props.title}
      aria-label={props['aria-label']}
      aria-pressed={props['aria-pressed']}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={props.autoFocus}
    >
      {props.children}
    </button>
  );
}

export function IconButton(props: { label: string; onClick?: (() => void) | undefined; children: ComponentChildren; disabled?: boolean | undefined; class?: string | undefined; pressed?: boolean | undefined }) {
  return (
    <button
      type="button"
      class={`icon-btn${props.class ? ` ${props.class}` : ''}`}
      aria-label={props.label}
      title={props.label}
      onClick={props.onClick}
      disabled={props.disabled}
      aria-pressed={props.pressed}
    >
      {props.children}
    </button>
  );
}

export function Card(props: { children: ComponentChildren; class?: string | undefined; title?: ComponentChildren | undefined; as?: 'section' | 'div' | 'article' | undefined }) {
  const Tag = props.as ?? 'section';
  return (
    <Tag class={`card${props.class ? ` ${props.class}` : ''}`}>
      {props.title && <h3 class="card-title">{props.title}</h3>}
      {props.children}
    </Tag>
  );
}

/** Focus-trapping modal dialog. */
export function Modal(props: { open: boolean; onClose?: (() => void) | undefined; title: string; children: ComponentChildren; class?: string | undefined; hideTitle?: boolean | undefined }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!props.open) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    (first ?? el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.onClose) {
        e.preventDefault();
        props.onClose();
      }
      if (e.key === 'Tab' && el) {
        const items = [...el.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')];
        if (!items.length) return;
        const a = items[0]!;
        const z = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [props.open]);
  if (!props.open) return <></>;
  return (
    <div class="modal-backdrop" onClick={(e) => e.target === e.currentTarget && props.onClose?.()} role="presentation">
      <div class={`modal${props.class ? ` ${props.class}` : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref} tabIndex={-1}>
        <h2 id={titleId} class={props.hideTitle ? 'visually-hidden' : 'modal-title'}>
          {props.title}
        </h2>
        {props.children}
        {props.onClose && (
          <button type="button" class="modal-close icon-btn" aria-label="Close" onClick={props.onClose}>
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

/** Side drawer (Case File on phones) or bottom drawer (ECHO). */
export function Drawer(props: { open: boolean; side: 'left' | 'bottom' | 'right'; onClose: () => void; title: string; children: ComponentChildren }) {
  return (
    <div class={`drawer-wrap${props.open ? ' open' : ''}`} aria-hidden={!props.open}>
      <div class="drawer-scrim" onClick={props.onClose} role="presentation" />
      <aside class={`drawer drawer-${props.side}`} aria-label={props.title} inert={!props.open}>
        <div class="drawer-head">
          <h2 class="drawer-title">{props.title}</h2>
          <IconButton label="Close" onClick={props.onClose}>
            ✕
          </IconButton>
        </div>
        {props.children}
      </aside>
    </div>
  );
}

export function BottomSheet(props: { children: ComponentChildren; class?: string | undefined; label: string }) {
  return (
    <section class={`bottom-sheet${props.class ? ` ${props.class}` : ''}`} aria-label={props.label}>
      <div class="bottom-sheet-grip" aria-hidden="true" />
      {props.children}
    </section>
  );
}

export function Tabs<T extends string>(props: { tabs: { id: T; label: string }[]; value: T; onChange: (id: T) => void; label: string }) {
  const onKey = (e: KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const n = props.tabs.length;
    const next = props.tabs[(i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n]!;
    props.onChange(next.id);
    (document.getElementById(`tab-${next.id}`) as HTMLElement | null)?.focus();
  };
  return (
    <div class="tabs" role="tablist" aria-label={props.label}>
      {props.tabs.map((t, i) => (
        <button
          key={t.id}
          id={`tab-${t.id}`}
          role="tab"
          type="button"
          aria-selected={props.value === t.id}
          tabIndex={props.value === t.id ? 0 : -1}
          class={`tab${props.value === t.id ? ' active' : ''}`}
          onClick={() => props.onChange(t.id)}
          onKeyDown={(e) => onKey(e as unknown as KeyboardEvent, i)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle(props: { label: string; checked: boolean; onChange: (v: boolean) => void; description?: string | undefined }) {
  const id = useId();
  return (
    <div class="toggle-row">
      <label for={id} class="toggle-label">
        <span>{props.label}</span>
        {props.description && <span class="toggle-desc">{props.description}</span>}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={props.checked}
        class={`toggle${props.checked ? ' on' : ''}`}
        onClick={() => props.onChange(!props.checked)}
      >
        <span class="toggle-knob" aria-hidden="true" />
      </button>
    </div>
  );
}

export function Slider(props: { label: string; value: number; min: number; max: number; step?: number | undefined; onChange: (v: number) => void; format?: ((v: number) => string) | undefined }) {
  const id = useId();
  return (
    <div class="slider-row">
      <label for={id}>{props.label}</label>
      <input
        id={id}
        type="range"
        min={props.min}
        max={props.max}
        step={props.step ?? 1}
        value={props.value}
        onInput={(e) => props.onChange(Number((e.target as HTMLInputElement).value))}
        aria-valuetext={props.format ? props.format(props.value) : String(props.value)}
      />
      <output for={id} class="slider-value">
        {props.format ? props.format(props.value) : props.value}
      </output>
    </div>
  );
}

/** Integer stepper (coefficients, counts). */
export function Stepper(props: { label: string; value: number; min?: number | undefined; max?: number | undefined; onChange: (v: number) => void; hideLabel?: boolean | undefined }) {
  const min = props.min ?? 0;
  const max = props.max ?? 99;
  return (
    <div class="stepper" role="group" aria-label={props.label}>
      <button type="button" class="stepper-btn" aria-label={`Decrease ${props.label}`} disabled={props.value <= min} onClick={() => props.onChange(Math.max(min, props.value - 1))}>
        −
      </button>
      <span class="stepper-value" aria-live="polite">
        {props.hideLabel ? '' : <span class="visually-hidden">{props.label}: </span>}
        {props.value}
      </span>
      <button type="button" class="stepper-btn" aria-label={`Increase ${props.label}`} disabled={props.value >= max} onClick={() => props.onChange(Math.min(max, props.value + 1))}>
        +
      </button>
    </div>
  );
}

/** Audit Heat meter (and general 0–100 meters). */
export function Meter(props: { label: string; value: number; max?: number | undefined; warnAt?: number | undefined; hidden?: boolean | undefined; class?: string | undefined }) {
  if (props.hidden) return <></>;
  const max = props.max ?? 100;
  const pct = Math.max(0, Math.min(100, (props.value / max) * 100));
  const level = props.value >= max ? 'max' : props.warnAt !== undefined && props.value >= props.warnAt ? 'warn' : 'ok';
  return (
    <div class={`meter meter-${level}${props.class ? ` ${props.class}` : ''}`}>
      <span class="meter-label">{props.label}</span>
      <span class="meter-track" role="meter" aria-label={props.label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={props.value}>
        <span class="meter-fill" style={{ width: `${pct}%` }} />
      </span>
      <span class="meter-value">{props.value}</span>
    </div>
  );
}

/** The FILED / AUDITED stamp. */
export function Stamp(props: { text: string; tone?: 'filed' | 'audited' | 'bureau' | undefined; animate?: boolean | undefined }) {
  return (
    <div class={`stamp stamp-${props.tone ?? 'filed'}${props.animate ? ' stamp-slam' : ''}`} role="img" aria-label={props.text}>
      <span>{props.text}</span>
    </div>
  );
}

export function ProgressPips(props: { total: number; done: number; current?: number | undefined; label: string }) {
  return (
    <span class="pips" role="img" aria-label={`${props.label}: ${props.done} of ${props.total}`}>
      {Array.from({ length: props.total }, (_, i) => (
        <span key={i} class={`pip${i < props.done ? ' done' : i === props.current ? ' current' : ''}`} />
      ))}
    </span>
  );
}

/** The in-stage Lab Log: every observation as plain text. */
export function LabLog(props: { entries: { text: string; tone?: string | undefined }[]; label?: string | undefined }) {
  const ref = useRef<HTMLOListElement>(null);
  useEffect(() => {
    ref.current?.lastElementChild?.scrollIntoView?.({ block: 'nearest' });
  }, [props.entries.length]);
  return (
    <section class="lablog" aria-label={props.label ?? 'Lab log'}>
      <h3 class="lablog-title">{props.label ?? 'Lab log'}</h3>
      {props.entries.length === 0 ? (
        <p class="lablog-empty">No observations yet.</p>
      ) : (
        // A scrolling region must be reachable by keyboard (WCAG 2.1.1; axe scrollable-region-focusable).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <ol ref={ref} aria-live="polite" tabIndex={0} aria-label="Observations">
          {props.entries.map((e, i) => (
            <li key={i} class={e.tone ? `lablog-${e.tone}` : undefined}>
              {e.text}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
